import { Box, Table, TableBody, TableCell, TableContainer, TableHead, TableRow } from '@material-ui/core'
import Checkbox from '@material-ui/core/Checkbox'
import SyncIcon from '@material-ui/icons/Sync'
import { capitalize } from 'lodash'
import { useState } from 'react'
import {
  DateField,
  Loading,
  maxValue,
  minValue,
  required,
  TextInput,
  useNotify,
  usePermissions,
  useRefresh,
} from 'react-admin'
import { Form } from 'react-final-form'
import { Grid, TextLeft } from 'shared/components/Base/Base'
import { FlexBox } from 'shared/components/Base/FlexBox'
import { BillingTable } from 'shared/components/Billing/BillingTable'
import { Button } from 'shared/components/Button/Button'
import { FormControlLabelStyled } from 'shared/components/Checkbox/Checkbox.styles'
import { DialogButton } from 'shared/components/Dialog/DialogButton'
import { SimpleDialog } from 'shared/components/Dialog/SimpleDialog'
import { ExternalLink } from 'shared/components/ExternalLink/ExternalLink'
import { Text } from 'shared/components/Text/Text'
import { Handler } from 'shared/helpers/typeHelper'
import {
  darkGray,
  error,
  errorD1,
  informationL1,
  kellyGreenD1,
  lightGray1,
  marigoldOrange,
  midGray,
  success,
  warning,
} from 'shared/theme/colors'
import { header3, paragraph3 } from 'shared/theme/typography'
import styled, { css } from 'styled-components'

import { stripebaseUrl } from '../../config/config'
import { useStripeSubscription } from '../../helpers/useStripeSubscription'
import {
  ProductSubscriptionStripeItemStatus,
  ProductSubscriptionStripeStatus,
} from '../../models/productSubscription'
import {
  cancelSubscription,
  disableCancelSubscription,
  Feature,
  lookupSubscription,
  syncSubscription,
  updateSubscription,
} from '../../models/subscription'
import { hasPermission, Permissions } from '../../Permissions'
import { TeamSubscriptionChangePricing } from './TeamSubscriptionChangePricing'

const defaultColors = ['#E3E8EC', midGray]

const SubscriptionStatus = {
  [ProductSubscriptionStripeStatus.NOT_APPLICABLE]: defaultColors,
  [ProductSubscriptionStripeStatus.ACTIVE]: [success, kellyGreenD1],
  [ProductSubscriptionStripeStatus.TRIALING]: [success, kellyGreenD1],
  [ProductSubscriptionStripeStatus.INCOMPLETE]: [warning, marigoldOrange],
  [ProductSubscriptionStripeStatus.INCOMPLETE_EXPIRED]: [error, errorD1],
  [ProductSubscriptionStripeStatus.CANCELED]: [error, errorD1],
  [ProductSubscriptionStripeStatus.UNPAID]: [error, errorD1],
  [ProductSubscriptionStripeStatus.PAST_DUE]: defaultColors,
}

const SubscriptionStatusText = {
  [ProductSubscriptionStripeStatus.NOT_APPLICABLE]: 'Not Applicable',
  [ProductSubscriptionStripeStatus.ACTIVE]: 'Active',
  [ProductSubscriptionStripeStatus.TRIALING]: 'Free trial',
  [ProductSubscriptionStripeStatus.INCOMPLETE]: 'Payment incomplete',
  [ProductSubscriptionStripeStatus.INCOMPLETE_EXPIRED]: 'Payment overdue',
  [ProductSubscriptionStripeStatus.CANCELED]: 'Canceled',
  [ProductSubscriptionStripeStatus.UNPAID]: 'Payment overdue',
  [ProductSubscriptionStripeStatus.PAST_DUE]: 'Payment overdue',
}

const SubscriptionItemStatus = {
  [ProductSubscriptionStripeItemStatus.PENDING]: defaultColors,
  [ProductSubscriptionStripeItemStatus.ACTIVE]: [success, kellyGreenD1],
  [ProductSubscriptionStripeItemStatus.OFFBOARDING]: [success, kellyGreenD1],
  [ProductSubscriptionStripeItemStatus.REMOVED]: [warning, marigoldOrange],
}

const SubscriptionItemStatusText = {
  [ProductSubscriptionStripeItemStatus.PENDING]: 'Pending',
  [ProductSubscriptionStripeItemStatus.ACTIVE]: 'Active',
  [ProductSubscriptionStripeItemStatus.OFFBOARDING]: 'Offboarding',
  [ProductSubscriptionStripeItemStatus.REMOVED]: 'Removed',
}

export function TeamStripeSubscription(props: any) {
  const { record: team } = props
  const [editQuantityForFeature, setEditQuantityForFeature] = useState<number | null>(null)
  const [isQuantityLoading, setIsQuantityLoading] = useState(false)
  const [isCanceling, setIsCanceling] = useState(false)
  const [isCancelLoading, setIsCancelLoading] = useState(false)
  const [isDisablingCanceling, setIsDisablingCanceling] = useState(false)
  const [isDisableCancelLoading, setIsDisableCancelLoading] = useState(false)
  const [isSyncLoading, setIsSyncLoading] = useState(false)
  const [isLookupLoading, setIsLookupLoading] = useState(false)
  const [isChangingPricing, setIsChangingPricing] = useState(false)
  const [isChangePricingLoading, setIsChangePricingLoading] = useState(false)
  const refresh = useRefresh()
  const notify = useNotify()
  const { permissions } = usePermissions()
  const canEditSubscription = hasPermission((permissions || []) as any, Permissions.EditStripeSubscription)
  const canViewStripeSubscriptionDevTools = hasPermission(
    (permissions || []) as any,
    Permissions.ViewStripeSubscriptionDevTools
  )
  const { subscription, refetchStripeSubscription } = useStripeSubscription({
    teamId: team?.id!!,
    enabled: Boolean(team?.product_subscription?.stripe_subscription_id),
  })

  if (!team.product_subscription?.stripe_subscription_id) {
    return (
      <Wrap>
        <Text variant="paragraph2">No subscription started.</Text>
        <Box mt={5} />
        {canViewStripeSubscriptionDevTools && (
          <Button
            variant="text"
            size="large"
            onClick={handleLookup}
            isLoading={isLookupLoading}
            icon={<SyncIcon />}
          >
            Lookup subscription
          </Button>
        )}
      </Wrap>
    )
  }

  if (!subscription || !team)
    return (
      <>
        <Box mt={5} />
        <Loading />
        <Box mt={5} />
      </>
    )

  const currentPrice = team.product_subscription.items.find(
    (item: any) => item?.product?.name === Feature.FLEX_STARTER_SEAT
  )?.product_price

  const items = team.product_subscription.items.filter((item: any) => Boolean(item.product_price))
  const status = team.product_subscription.stripe_subscription_status as ProductSubscriptionStripeStatus
  const isActiveStatus = [
    ProductSubscriptionStripeStatus.ACTIVE,
    ProductSubscriptionStripeStatus.TRIALING,
  ].includes(status)
  const canRefundSubscription =
    hasPermission((permissions || []) as any, Permissions.RefundStripeSubscription) &&
    status === ProductSubscriptionStripeStatus.ACTIVE

  async function handleChangeQuantity(quantity: string | number) {
    try {
      setIsQuantityLoading(true)
      await updateSubscription(team?.id!, { quantity, feature: editQuantityForFeature! })
      await refresh()
    } catch (err: any) {
      if (err?.response?.data) notify(JSON.stringify(err?.response?.data), { type: 'error' })
      else notify('Something went wrong', { type: 'error' })
    }
    setIsQuantityLoading(false)
  }

  async function handleCancel({
    refund,
    cancelAtPeriodEnd,
  }: {
    refund?: boolean
    cancelAtPeriodEnd?: boolean
  }) {
    try {
      setIsCancelLoading(true)
      await cancelSubscription(team?.id!, { refund, cancelAtPeriodEnd })
      await refresh()
    } catch (err: any) {
      if (err?.response?.data) notify(JSON.stringify(err?.response?.data), { type: 'error' })
      else notify('Something went wrong', { type: 'error' })
    }
    setIsCancelLoading(false)
  }

  async function handleDisableCancel() {
    try {
      setIsDisableCancelLoading(true)
      await disableCancelSubscription(team?.id!)
      await refresh()
    } catch (err: any) {
      if (err?.response?.data) notify(JSON.stringify(err?.response?.data), { type: 'error' })
      else notify('Something went wrong', { type: 'error' })
    }
    setIsDisableCancelLoading(false)
  }

  async function handleSync() {
    try {
      setIsSyncLoading(true)
      await syncSubscription(team?.id!)
      await refresh()
    } catch (err: any) {
      if (err?.response?.data) notify(JSON.stringify(err?.response?.data), { type: 'error' })
      else notify('Something went wrong', { type: 'error' })
    }
    setIsSyncLoading(false)
  }

  async function handleLookup() {
    try {
      setIsLookupLoading(true)
      await lookupSubscription(team?.id!)
      await refresh()
    } catch (err: any) {
      if (err?.response?.data) notify(JSON.stringify(err?.response?.data), { type: 'error' })
      else notify('Something went wrong', { type: 'error' })
    }
    setIsLookupLoading(false)
  }

  async function changePricing() {
    try {
      setIsChangePricingLoading(true)
      await refetchStripeSubscription()
      await refresh()
    } catch (err: any) {
      if (err?.response?.data) notify(JSON.stringify(err?.response?.data), { type: 'error' })
      else notify('Something went wrong', { type: 'error' })
    }
    setIsChangePricingLoading(false)
  }

  function getTableWidth() {
    if (canEditSubscription && canViewStripeSubscriptionDevTools) return '1300px'
    return '900px'
  }

  return (
    <Wrap>
      <Grid columns="550px 500px" gap="30px">
        <Text variant="uppercaseLabel2" fontColor={darkGray}>
          Subscription details
        </Text>
        <Box mt={2} />
        <Grid columns="230px 320px" gap="10px 30px">
          <Text variant="header3" fontColor={lightGray1}>
            Status
          </Text>
          <div>
            <ExternalLink
              href={`${stripebaseUrl}subscriptions/${team.product_subscription?.stripe_subscription_id}`}
            >
              <TeamStripeSubscriptionStatus status={status} cancel_at={subscription.cancel_at!} />
            </ExternalLink>
          </div>

          {canViewStripeSubscriptionDevTools && (
            <>
              <Text variant="header3" fontColor={lightGray1}>
                Stripe customer
              </Text>
              <div>
                <ExternalLink
                  href={`${stripebaseUrl}customers/${team.stripe_subscription_account_customer_id}`}
                >
                  <Text variant="label2" fontColor={darkGray} fontWeight={400}>
                    {team.stripe_subscription_account_customer_id}
                  </Text>
                </ExternalLink>
              </div>
            </>
          )}

          {canViewStripeSubscriptionDevTools && (
            <>
              <Text variant="header3" fontColor={lightGray1}>
                Stripe subscription
              </Text>
              <div>
                <ExternalLink
                  href={`${stripebaseUrl}subscriptions/${team.product_subscription?.stripe_subscription_id}`}
                >
                  <Text variant="label2" fontColor={darkGray} fontWeight={400}>
                    {team.product_subscription?.stripe_subscription_id}
                  </Text>
                </ExternalLink>
              </div>
            </>
          )}

          {subscription.created && (
            <>
              <Text variant="header3" fontColor={lightGray1}>
                Subscription started
              </Text>
              <Text variant="label2" fontColor={darkGray} fontWeight={400}>
                <DateField
                  record={subscription as any}
                  showTime={true}
                  source="created"
                  options={{ timeZoneName: 'short' }}
                />
              </Text>
            </>
          )}
          {subscription.current_period_end && (
            <>
              <Text variant="header3" fontColor={lightGray1}>
                Subscription renews
              </Text>
              <Text variant="label2" fontColor={darkGray} fontWeight={400}>
                <DateField
                  record={subscription as any}
                  showTime={true}
                  source="current_period_end"
                  options={{ timeZoneName: 'short' }}
                />
              </Text>
            </>
          )}
          {subscription.trial_end && (
            <>
              <Text variant="header3" fontColor={lightGray1}>
                Subscription trial end
              </Text>
              <Text variant="label2" fontColor={darkGray} fontWeight={400}>
                <DateField
                  record={subscription as any}
                  showTime={true}
                  source="trial_end"
                  options={{ timeZoneName: 'short' }}
                />
              </Text>
            </>
          )}
          {subscription.cancel_at && (
            <>
              <Text variant="header3" fontColor={errorD1}>
                Subscription will cancel at
              </Text>
              <Text variant="label2" fontColor={errorD1} fontWeight={400}>
                <DateField
                  record={subscription as any}
                  showTime={true}
                  source="cancel_at"
                  options={{ timeZoneName: 'short' }}
                />
              </Text>
            </>
          )}
          {subscription.canceled_at && (
            <>
              <Text variant="header3" fontColor={lightGray1}>
                Subscription canceled at
              </Text>
              <Text variant="label2" fontColor={darkGray} fontWeight={400}>
                <DateField
                  record={subscription as any}
                  showTime={true}
                  source="canceled_at"
                  options={{ timeZoneName: 'short' }}
                />
              </Text>
            </>
          )}
          {Boolean(team.product_subscription?.stripe_subscription_cancel_reasons?.length) && (
            <>
              <Text variant="header3" fontColor={lightGray1}>
                Subscription cancel reasons
              </Text>
              <Text variant="label2" fontColor={darkGray} fontWeight={400}>
                {team.product_subscription?.stripe_subscription_cancel_reasons.map((reason: string) => (
                  <div>{reason}</div>
                ))}
              </Text>
            </>
          )}
          {Boolean(team.product_subscription?.stripe_subscription_cancel_reasons_detail) && (
            <>
              <Text variant="header3" fontColor={lightGray1}>
                Subscription cancel detail
              </Text>
              <Text variant="label2" fontColor={darkGray} fontWeight={400}>
                {team.product_subscription?.stripe_subscription_cancel_reasons_detail}
              </Text>
            </>
          )}
          {subscription.payment_method && (
            <>
              <Text variant="header3" fontColor={lightGray1}>
                Payment method
              </Text>
              <FlexBox gap="20px">
                <Text variant="label2" fontColor={darkGray} fontWeight={400}>
                  {capitalize(subscription.payment_method.brand)} {subscription.payment_method.type} (****{' '}
                  {subscription.payment_method.last4})
                </Text>
                {canEditSubscription && (
                  <Button
                    variant="text"
                    size="large"
                    href={`${stripebaseUrl}subscriptions/${team.product_subscription?.stripe_subscription_id}/edit`}
                  >
                    Edit
                  </Button>
                )}
              </FlexBox>
            </>
          )}
        </Grid>
        <div>
          <FlexBox gap="30px" isColumn width="240px">
            <Button
              variant="primary"
              size="large"
              href={`${stripebaseUrl}subscriptions/${team.product_subscription?.stripe_subscription_id}`}
            >
              See payment history
            </Button>

            {canEditSubscription && isActiveStatus && (
              <Button
                variant="secondary"
                size="large"
                onClick={() => setIsChangingPricing(true)}
                isLoading={isChangePricingLoading}
              >
                Change starter pricing
              </Button>
            )}

            {canEditSubscription && subscription.cancel_at && isActiveStatus && (
              <Button
                variant="secondary"
                size="large"
                onClick={() => setIsDisablingCanceling(true)}
                isLoading={isDisableCancelLoading}
              >
                Disable cancelation
              </Button>
            )}

            {canEditSubscription && status !== ProductSubscriptionStripeStatus.CANCELED && (
              <Button
                variant="text"
                size="large"
                onClick={() => setIsCanceling(true)}
                isLoading={isCancelLoading}
              >
                Cancel subscription
              </Button>
            )}

            {subscription.upcomingInvoice && (
              <DialogButton
                width="500px"
                dialogContent={
                  <TextLeft>
                    <BillingTable invoice={subscription.upcomingInvoice} />
                  </TextLeft>
                }
              >
                <Button
                  variant="text"
                  size="large"
                  onClick={() => setIsCanceling(true)}
                  isLoading={isCancelLoading}
                >
                  View upcoming invoice
                </Button>
              </DialogButton>
            )}
          </FlexBox>
        </div>
      </Grid>

      <Box mt={3} />
      <Text variant="uppercaseLabel2" fontColor={darkGray}>
        Products
      </Text>
      <Grid columns={getTableWidth()}>
        <TableContainer>
          <Table>
            <TableHead>
              <TableRow>
                <StyledTableCell>Product</StyledTableCell>
                <StyledTableCell>Price</StyledTableCell>
                <StyledTableCell>Quantity</StyledTableCell>
                <StyledTableCell>Billing Period</StyledTableCell>
                <StyledTableCell>Description</StyledTableCell>
                <StyledTableCell>Status</StyledTableCell>
                <StyledTableCell></StyledTableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {items.map((item: any) => (
                <TableRow>
                  <TableCell>{item.product.name}</TableCell>
                  <TableCell>${item?.product_price?.amount}</TableCell>
                  <TableCell>{item?.quantity}</TableCell>
                  <TableCell>{item?.product_price?.billing_period}</TableCell>
                  <TableCell>{item?.product_price?.description}</TableCell>
                  <TableCell>
                    <Status
                      $backgroundColor={
                        (SubscriptionItemStatus[item?.status as ProductSubscriptionStripeItemStatus] ||
                          defaultColors)[0]
                      }
                      $color={
                        (SubscriptionItemStatus[item?.status as ProductSubscriptionStripeItemStatus] ||
                          defaultColors)[1]
                      }
                      data-original-status={item?.status}
                    >
                      {SubscriptionItemStatusText[item?.status as ProductSubscriptionStripeItemStatus]}
                    </Status>
                  </TableCell>
                  <TableCell>
                    <FlexBox gap="10px">
                      {canViewStripeSubscriptionDevTools && (
                        <>
                          <StyledExternalLink
                            href={`${stripebaseUrl}products/${item.product.stripe_product_id}`}
                          >
                            <Text
                              variant="label2"
                              fontColor={darkGray}
                              fontWeight={400}
                              data-testid={item.product.stripe_product_id}
                            >
                              View product in stripe
                            </Text>
                          </StyledExternalLink>
                          <StyledExternalLink
                            href={`${stripebaseUrl}prices/${item.product_price.stripe_price_id}`}
                          >
                            <Text
                              variant="label2"
                              fontColor={darkGray}
                              fontWeight={400}
                              data-testid={item.product_price.stripe_price_id}
                            >
                              View price in stripe
                            </Text>
                          </StyledExternalLink>
                        </>
                      )}
                      <div>
                        {canEditSubscription && (
                          <Button
                            variant="secondary"
                            size="medium"
                            onClick={() => setEditQuantityForFeature(item.product.feature_id)}
                          >
                            Edit quantity
                          </Button>
                        )}
                      </div>
                    </FlexBox>
                  </TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </TableContainer>
      </Grid>
      <Box mt={5} />

      {canViewStripeSubscriptionDevTools && (
        <Button
          variant="text"
          size="large"
          onClick={handleSync}
          isLoading={isSyncLoading}
          icon={<SyncIcon />}
        >
          Refresh sync with Stripe
        </Button>
      )}

      {editQuantityForFeature && (
        <UpdateQuantity
          items={items}
          feature={editQuantityForFeature}
          isLoading={isQuantityLoading}
          onSuccess={handleChangeQuantity}
          onClose={() => setEditQuantityForFeature(null)}
        />
      )}

      {isCanceling && (
        <CancelSubscription
          isLoading={isCancelLoading}
          onSuccess={handleCancel}
          onClose={() => setIsCanceling(false)}
          canRefundSubscription={canRefundSubscription}
        />
      )}

      {isDisablingCanceling && (
        <DisableCancelSubscription
          isLoading={isDisableCancelLoading}
          onSuccess={handleDisableCancel}
          onClose={() => setIsDisablingCanceling(false)}
        />
      )}

      {isChangingPricing && (
        <TeamSubscriptionChangePricing
          team={team}
          currentPrice={currentPrice?.id}
          isLoading={isChangePricingLoading}
          onSuccess={changePricing}
          onClose={() => setIsChangingPricing(false)}
        />
      )}
    </Wrap>
  )
}

function UpdateQuantity({
  items,
  feature,
  onSuccess,
  onClose,
  isLoading,
}: {
  items: any
  feature: number
  onSuccess: (values: any) => void
  onClose: Handler
  isLoading: boolean
}) {
  const initialQuantity = items.find((i: any) => i.product.feature_id === feature)?.quantity || 0

  function handleSubmit(values: any) {
    onSuccess(values.quantity!)
  }

  function renderForm(formProps: any) {
    return (
      <>
        <Text variant="header2" align="left">
          Quantity
        </Text>
        <Box mt={4} />

        <TextInput
          source="quantity"
          label="Amount"
          validate={[required(), minValue(1), maxValue(500)]}
          fullWidth
        />

        <Box display="flex" flexDirection="column" alignItems="center">
          <Box mt={4} />
          <Button type="submit" size="large" onClick={formProps.handleSubmit} isLoading={isLoading}>
            Save
          </Button>
          <Box mt={3} />
          <Button variant="text" onClick={onClose}>
            Go Back
          </Button>
        </Box>
      </>
    )
  }

  return (
    <SimpleDialog isOpen onClose={onClose}>
      <div style={{ width: '390px' }}>
        <Form
          initialValues={{
            quantity: initialQuantity,
          }}
          onSubmit={handleSubmit}
          render={renderForm}
        />
      </div>
    </SimpleDialog>
  )
}

function CancelSubscription({
  onSuccess,
  onClose,
  isLoading,
  canRefundSubscription,
}: {
  onSuccess: (values: { refund?: boolean; cancelAtPeriodEnd?: boolean }) => void
  onClose: Handler
  isLoading: boolean
  canRefundSubscription: boolean
}) {
  const [cancelAtPeriodEnd, setCancelAtPeriodEnd] = useState<boolean | undefined>(true)
  const [refund, setRefund] = useState<boolean | undefined>(false)

  function handleSubmit() {
    onSuccess({ refund, cancelAtPeriodEnd })
  }

  return (
    <SimpleDialog isOpen onClose={onClose}>
      <div style={{ width: '390px' }}>
        <>
          <Text variant="header2" align="left">
            Are you sure to cancel this subscription?
          </Text>
          <Box mt={4} />

          <FormControlLabelStyled
            control={
              <Checkbox
                checked={cancelAtPeriodEnd}
                onChange={(_e, checked: boolean) => {
                  setCancelAtPeriodEnd(checked)
                  if (checked) {
                    setRefund(false)
                  }
                }}
                name="cancelAtPeriodEnd"
                color="primary"
              />
            }
            label={
              <CheckboxLabel>
                <Text variant="header3">Cancel at the end of billing period</Text>
                <Text variant="paragraph2" fontColor={lightGray1} mt={1}>
                  Will keep current subscription active until the current billing period ends. After that it
                  will get canceled automatically.
                </Text>
              </CheckboxLabel>
            }
          />

          {canRefundSubscription && (
            <FormControlLabelStyled
              control={
                <Checkbox
                  checked={refund}
                  onChange={(_e, checked: boolean) => {
                    setRefund(checked)
                    if (checked) {
                      setCancelAtPeriodEnd(false)
                    }
                  }}
                  name="refund"
                  color="primary"
                />
              }
              label={
                <CheckboxLabel>
                  <Text variant="header3">Refund</Text>
                  <Text variant="paragraph2" fontColor={lightGray1} mt={1}>
                    Will cancel immediately and issue a prorate refund.
                  </Text>
                </CheckboxLabel>
              }
            />
          )}

          <Box mt={4} />

          <Box display="flex" flexDirection="column" alignItems="center">
            <Box mt={4} />
            <Button type="submit" size="large" onClick={handleSubmit} isLoading={isLoading}>
              Cancel Subscription
            </Button>
            <Box mt={3} />
            <Button variant="text" onClick={onClose}>
              Go Back
            </Button>
          </Box>
        </>
      </div>
    </SimpleDialog>
  )
}

export function TeamStripeSubscriptionStatus({
  status,
  cancel_at,
  size,
}: {
  status: ProductSubscriptionStripeStatus
  cancel_at?: boolean | number
  size?: 'small' | 'regular'
}) {
  if (!status) return null
  return (
    <Status
      $backgroundColor={(SubscriptionStatus[status] || defaultColors)[0]}
      $color={(SubscriptionStatus[status] || defaultColors)[1]}
      $size={size}
      data-original-status={status}
    >
      {SubscriptionStatusText[status]}
      {cancel_at && ' (automatic cancelation)'}
    </Status>
  )
}

function DisableCancelSubscription({
  onSuccess,
  onClose,
  isLoading,
}: {
  onSuccess: Handler
  onClose: Handler
  isLoading: boolean
}) {
  return (
    <SimpleDialog isOpen onClose={onClose}>
      <div style={{ width: '390px' }}>
        <>
          <Text variant="header2" align="left">
            Are you sure to disable automatic cancel of this subscription?
          </Text>

          <Box display="flex" flexDirection="column" alignItems="center">
            <Box mt={4} />
            <Button type="submit" size="large" onClick={onSuccess} isLoading={isLoading}>
              Disable Cancelation
            </Button>
            <Box mt={3} />
            <Button variant="text" onClick={onClose}>
              Go Back
            </Button>
          </Box>
        </>
      </div>
    </SimpleDialog>
  )
}

const Wrap = styled.div`
  padding: 20px;

  ${Text} .MuiTypography-body2 {
    font-size: inherit;
    font-family: inherit;
    line-height: inherit;
    font-weight: inherit;
    letter-spacing: inherit;
  }
`

const Status = styled.div<{ $color: string; $backgroundColor: string; $size?: 'small' | 'regular' }>`
  ${header3};
  padding: 3px 10px 2px;
  display: inline-block;
  border-radius: 3px;

  ${(p) => css`
    color: ${p.$color};
    background-color: ${p.$backgroundColor};
  `}

  ${(p) =>
    p.$size === 'small' &&
    css`
      ${paragraph3};
      white-space: nowrap;
      font-weight: 600;
      padding: 2px 5px 1px;
    `}
`

const StyledTableCell = styled(TableCell)`
  font-weight: 600;
`

const StyledExternalLink = styled(ExternalLink)`
  padding: 5px;

  &:hover {
    background: ${informationL1};
  }
`

const CheckboxLabel = styled.div`
  text-align: left;
`
